/* eslint-disable react/button-has-type */
import L from 'leaflet'
import { createRoot } from 'react-dom/client'
import PropTypes from 'prop-types'
import { NewPinButtonWrapper, PinButtonContainer } from '../styledComponents'
import {
  NewPinCreateActionButtons,
  NewPinCancelActionButtons
} from '../../../components/Button/NewPinActionButtons'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Marker } from 'react-leaflet'
import PinComponent from '../../../components/stories/atoms/pins'
import { pinsStatusVariantOptions } from '../../../components/stories/atoms/pins/constants'
import ReactDOMServer from 'react-dom/server'
import { pinsColors } from '../../../components/stories/fundamentals/colors-styles/constants'
import { v4 as uuid } from 'uuid'

const pinStatus = {
  OPEN: 'Open',
  CLOSED: 'Closed',
  INACTIVE: 'Inactive'
}

const PinIndicatorCurrent = {
  OPEN: 'Open',
  DONE: 'Done',
  REJECTED: 'Rejected',
  CLOSED: 'Closed',
  INACTIVE: 'Inactive',
  DUE: 'Due'
}

export const handleIconIndicator = (state, indicator, progress) => {
  if (state === pinStatus.CLOSED) {
    return pinsStatusVariantOptions.CLOSED
  }

  if (progress === 100) {
    return pinsStatusVariantOptions.COMPLETED
  }

  // Mapping indicators to corresponding statuses
  const statusMap = {
    [PinIndicatorCurrent.OPEN]: pinsStatusVariantOptions.PROGRESS,
    [PinIndicatorCurrent.DUE]: pinsStatusVariantOptions.OVERDUE,
    [PinIndicatorCurrent.REJECTED]: pinsStatusVariantOptions.REJECTED
  }

  // Returns the corresponding status or a default value
  return statusMap[indicator] || pinsStatusVariantOptions.PROGRESS
}

export const handleIconPinWithoutIndicator = (state) => {
  if (state === pinStatus.CLOSED) {
    return pinsStatusVariantOptions.CLOSED
  }

  return undefined
}

export const generateMapPinIcon = (pin, reduced, selected) => {
  const uid = `pin-item-${uuid()}`

  const handleGenerate = (hasTasks) => {
    if (hasTasks) {
      return ReactDOMServer.renderToString(
        <PinComponent
          variant={`gradient-${pin.color.toLowerCase()}`}
          gradientId={uid}
          isInactive={
            (reduced || (pin.state && pin.state === 'Inactive')) && !selected
          }
          percentage={pin.progress}
          size={60}
          iconStatus={handleIconIndicator(
            pin.state,
            pin.indicatorCurrent,
            pin.progress
          )}
        />
      )
    } else {
      return ReactDOMServer.renderToString(
        <PinComponent
          variant={`gradient-${pin.color.toLowerCase()}`}
          gradientId={uid}
          isInactive={
            (reduced || (pin.state && pin.state === 'Inactive')) && !selected
          }
          size={60}
          iconStatus={handleIconPinWithoutIndicator(pin.state)}
        />
      )
    }
  }

  const hasTasks = pin.totalTasks > 0

  const html = handleGenerate(hasTasks)

  const divIcon = new L.divIcon({
    iconSize: new L.Point(60, 60),
    className: '',
    html,
    iconAnchor: [30, 60]
  })

  return divIcon
}

export const generatePinPosition = ({ x, y }, map) =>
  map.unproject([x, y], map.getMaxZoom())

export const generatePinClusterOptions = (maxLevel) => {
  const uid = `pin-cluster-${uuid()}`
  
  const handlePinCluster = (cluster) =>
    ReactDOMServer.renderToString(
      <PinComponent
        isInactive={false}
        counter={Number(cluster.getChildCount())}
        colorCounter={'white'}
        size={60}
        gradientId={uid}
      />
    )

  return {
    spiderfyOnMaxZoom: false,
    showCoverageOnHover: false,
    disableClusteringAtZoom: maxLevel,
    animateAddingMarkers: true,
    animate: true,
    maxClusterRadius: 40,
    iconCreateFunction: (cluster) =>
      L.divIcon({
        iconSize: new L.Point(60, 60),
        className: '',
        html: handlePinCluster(cluster),
        iconAnchor: [30, 60]
      })
  }
}

export const generateNewPinPosition = (map, fullBounds) => {
  const center = map.getCenter()

  let { lat } = center
  let { lng } = center
  const northEastLat = fullBounds._northEast.lat // eslint-disable-line no-underscore-dangle
  const southWestLat = fullBounds._southWest.lat // eslint-disable-line no-underscore-dangle
  const northEastLng = fullBounds._northEast.lng // eslint-disable-line no-underscore-dangle
  const southWestLng = fullBounds._southWest.lng // eslint-disable-line no-underscore-dangle
  if (lat > northEastLat) {
    lat = northEastLat
  } else if (lat < southWestLat) {
    lat = southWestLat
  }
  if (lng > northEastLng) {
    lng = northEastLng
  } else if (lng < southWestLng) {
    lng = southWestLng
  }

  return [lat, lng]
}

// noinspection JSPotentiallyInvalidConstructorUsage
export const generateNewPinIcon = (color) => {
  const uid = `pin-new-${uuid()}`
  const html = ReactDOMServer.renderToString(
    <PinComponent
      variant={`gradient-${color?.toLowerCase() ?? 'orange'}`}
      size={60}
      gradientId={uid}
    />
  )

  const divIcon = new L.divIcon({
    iconSize: new L.Point(60, 60),
    className: '',
    html,
    iconAnchor: [30, 60]
  })

  return divIcon
}

export const generateMovePinIcon = (pin) => {
  const uid = `pin-move-${uuid()}`
  const html = ReactDOMServer.renderToString(
    <PinComponent
      variant={`gradient-${pin.color.toLowerCase()}`}
      isInactive={pin.state && pin.state === 'Inactive'}
      percentage={pin.progress}
      size={60}
      gradientId={uid}
      iconStatus={handleIconIndicator(
        pin.state,
        pin.indicatorCurrent,
        pin.progress
      )}
    />
  )

  return new L.divIcon({
    iconSize: new L.Point(60, 60),
    className: '',
    html,
    iconAnchor: [30, 60]
  })
}

// Components to render the pin with buttons.
const PinWithButtons = ({ pinColor, cancelCreatePin, createPin }) => (
  <PinButtonContainer>
    <PinComponent
      variant={`gradient-${pinColor?.toLowerCase() ?? 'orange'}`}
      size={60}
    />
    <NewPinButtonWrapper>
      <NewPinCancelActionButtons
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          cancelCreatePin()
        }}
        color={pinsColors[pinColor]}
      />
      <NewPinCreateActionButtons
        onClick={createPin}
        color={pinsColors[pinColor]}
      />
    </NewPinButtonWrapper>
  </PinButtonContainer>
)

const NewPin = ({ pinColor, cancelCreatePin, createPin, ...markerProps }) => {
  const containerRef = useRef(document.createElement('div'))
  const rootRef = useRef(null)
  const mountedRef = useRef(true)

  const handleCancel = useCallback(() => {
    cancelCreatePin()
    if (rootRef.current) {
      rootRef.current.unmount()
      mountedRef.current = false
    }
  }, [cancelCreatePin])

  const handleCreate = useCallback(() => {
    createPin()
    if (rootRef.current) {
      rootRef.current.unmount()
      mountedRef.current = false
    }
  }, [createPin])

  // Inicialização do root
  useEffect(() => {
    if (!rootRef.current) {
      rootRef.current = createRoot(containerRef.current)
    }
  }, [])

  // Atualização do conteúdo renderizado
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.render(
        <PinWithButtons
          pinColor={pinColor}
          cancelCreatePin={handleCancel}
          createPin={handleCreate}
        />
      )
    }
  }, [pinColor, cancelCreatePin, createPin])

  const divIcon = useMemo(
    () =>
      new L.divIcon({
        iconSize: new L.Point(60, 60),
        className: '',
        html: containerRef.current,
        iconAnchor: [30, 60]
      }),
    []
  )

  return <Marker {...markerProps} icon={divIcon} />
}

export default NewPin

export const PinCluster = ({ cluster, options, children }) =>
  cluster ? (
    <MarkerClusterGroup {...options}>{children}</MarkerClusterGroup>
  ) : (
    <div>{children}</div>
  )

PinCluster.propTypes = {
  cluster: PropTypes.bool,
  options: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
}
